// ContentPage — Kanban-style content pipeline. Visual preserved from original;
// content sourced from page_content row at slug `system-content-board`.

import { useQuery } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";
import { useApi } from "@/lib/api";
import { ColorBlock, type ColorBlockTint } from "@/components/page";
import { Button } from "@/components/ui/button";

type ContentCard = {
  id: string;
  title: string;
  kind: string;
  updated: string;
  body?: string;
};

type ContentColumn = {
  title: string;
  tint: ColorBlockTint;
  empty: string;
  cards: ContentCard[];
};

type ContentBoard = {
  kind: "content-board-v1";
  header: { eyebrow?: string; title: string; lede?: string };
  newButtonLabel?: string;
  columns: ContentColumn[];
};

const SLUG = "system-content-board";

function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-svh -mx-6 -my-8 px-6 py-6 sm:py-8 bg-stone-50">
      <div className="mx-auto max-w-5xl">{children}</div>
    </div>
  );
}

function Column({ col }: { col: ContentColumn }) {
  return (
    <ColorBlock
      tint={col.tint}
      size="md"
      eyebrow={col.title}
      aside={
        <span className="text-xl font-bold tabular-nums text-stone-900">
          {col.cards.length}
        </span>
      }
    >
      {col.cards.length === 0 ? (
        <p className="text-sm text-stone-700/80 leading-relaxed">{col.empty}</p>
      ) : (
        <ul className="space-y-3">
          {col.cards.map((c) => (
            <li
              key={c.id}
              className="rounded-2xl bg-white px-4 py-3 cursor-pointer hover:shadow-sm transition-shadow"
            >
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-500">
                  {c.kind}
                </span>
                <span className="text-[10px] text-stone-500">{c.updated}</span>
              </div>
              <h4 className="text-sm font-semibold text-stone-900 leading-snug mb-1.5">
                {c.title}
              </h4>
              {c.body ? (
                <p className="text-xs text-stone-600 leading-relaxed line-clamp-3">
                  {c.body}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </ColorBlock>
  );
}

export function ContentPage() {
  const api = useApi();

  const { data, isLoading, error } = useQuery({
    queryKey: ["kb", SLUG],
    queryFn: async (): Promise<ContentBoard> => {
      const res = await api(`/api/kb/${SLUG}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { content: ContentBoard };
      return json.content;
    },
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <PageFrame>
        <div className="flex items-center gap-2 p-2 text-sm text-stone-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          טוען…
        </div>
      </PageFrame>
    );
  }

  if (error || !data) {
    return (
      <PageFrame>
        <div className="rounded-2xl border border-rose-300/60 bg-rose-50/40 p-5">
          <div className="text-sm font-semibold text-rose-700">לא ניתן לטעון את העמוד</div>
        </div>
      </PageFrame>
    );
  }

  return (
    <PageFrame>
      <header className="flex items-end justify-between gap-6 flex-wrap mb-6">
        <div>
          {data.header.eyebrow && (
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">
              {data.header.eyebrow}
            </p>
          )}
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-[-0.02em] leading-tight text-stone-900">
            {data.header.title}
          </h1>
          {data.header.lede && (
            <p className="mt-3 text-base text-stone-600 max-w-xl leading-relaxed">
              {data.header.lede}
            </p>
          )}
        </div>
        <Button
          disabled
          size="lg"
          className="rounded-full h-12 px-6 gap-2 bg-stone-900 hover:bg-stone-800"
        >
          <Plus className="h-4 w-4" />
          {data.newButtonLabel ?? "תוכן חדש"}
        </Button>
      </header>

      <div className="grid gap-5 md:grid-cols-3">
        {data.columns.map((col, i) => (
          <Column key={i} col={col} />
        ))}
      </div>
    </PageFrame>
  );
}
